import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { EkycTier } from '../configuration/configuration.model';

/**
 * Reference-data lookups for the wizard.
 *
 * Endpoints mirror the retail journey (contracts-1 §6, Appendix A). In the showcase every method
 * is overridden by MockEkycMetaService; the real URLs are kept so the seam is obvious.
 */
@Injectable({ providedIn: 'root' })
export class EkycMetaService {
  private readonly meta: string;
  private readonly account: string;

  constructor(protected readonly http: HttpClient, applicationConfigService: ApplicationConfigService) {
    this.meta = applicationConfigService.getEndpointFor('meta');
    this.account = applicationConfigService.getEndpointFor('account/account-opening');
  }

  divisions(accountClass: string): Observable<any> {
    return this.http.get<any>(`${this.meta}/account-opening/divisions?accountClass=${accountClass}`);
  }

  districts(accountClass: string, divisionId: string): Observable<any> {
    return this.http.get<any>(`${this.meta}/account-opening/districts?accountClass=${accountClass}&divisionId=${divisionId}`);
  }

  thanas(accountClass: string, districtId: string): Observable<any> {
    return this.http.get<any>(`${this.meta}/account-opening/thana-upazilas?accountClass=${accountClass}&districtId=${districtId}`);
  }

  branches(accountClass: string, districtId: string): Observable<any> {
    return this.http.get<any>(`${this.meta}/account-opening/own-bank-branches?accountClass=${accountClass}&districtId=${districtId}`);
  }

  religions(accountClass: string): Observable<any> {
    return this.http.get<any>(`${this.meta}/application-setting/format/code/ACCOUNT_OPENING_RELIGION_${accountClass}`);
  }

  professions(): Observable<any> {
    return this.http.get<any>(`${this.meta}/application-setting/format/code/ACCOUNT_OPENING_PROFESSION_LIST`);
  }

  occupationCategories(accountClass: string): Observable<any> {
    return this.http.get<any>(`${this.meta}/account-opening/occupation-categories?accountClass=${accountClass}`);
  }

  sectors(accountClass: string): Observable<any> {
    return this.http.get<any>(`${this.meta}/account-opening/sectors?accountClass=${accountClass}`);
  }

  incomeSources(): Observable<any> {
    return this.http.get<any>(`${this.meta}/application-setting/format/code/ACCOUNT_OPENING_SOURCE_OF_FUND_LIST`);
  }

  nomineeRelations(): Observable<any> {
    return this.http.get<any>(`${this.meta}/application-setting/format/code/ACCOUNT_OPENING_NOMINEE_RELATION_LIST`);
  }

  /**
   * Eligible account products.
   *
   * `tier` is the Step 1 e-KYC type. Amendment 1 (Q1) makes the tier binding: the slab list this
   * feeds is filtered by it, so an over-limit profile is never selectable.
   */
  accountProducts(accountClass: string, tier: EkycTier): Observable<any> {
    return this.http.get<any>(`${this.account}/user-eligible-products?accountClass=${accountClass}&type=ACCOUNT&ekycType=${tier}`);
  }

  cardProducts(accountClass: string, accountProductCode: string): Observable<any> {
    return this.http.get<any>(
      `${this.account}/user-eligible-products?accountClass=${accountClass}&type=CARD&selectedAccountProductTypeCode=${accountProductCode}`,
    );
  }

  /** Transaction profiles available for the chosen tier — the Q1 filter. */
  transactionProfiles(tier: EkycTier): Observable<any> {
    return this.http.get<any>(`${this.account}/transaction-profiles?ekycType=${tier}`);
  }

  /** CBS dedupe by NID and mobile — contracts-2 B3. */
  existingAccounts(nid: string, mobile: string, accountClass: string): Observable<any> {
    return this.http.get<any>(`${this.account}/pre-validation?nid=${nid}&mobile=${mobile}&accountClass=${accountClass}`);
  }

  // ---- identity verification ------------------------------------------------------------------

  parseNid(side: 'front' | 'back', payload: FormData): Observable<any> {
    return this.http.post<any>(`${this.meta}/../user/ekyc-v2/parse-nid-${side}/logged-in-user`, payload);
  }

  validateEcInfo(payload: unknown): Observable<any> {
    return this.http.post<any>(`${this.meta}/../user/ekyc-v3/validate-ec-info/logged-in-user`, payload);
  }

  faceMatch(payload: unknown): Observable<any> {
    return this.http.post<any>(`${this.meta}/../user/ekyc-v3/face-match/logged-in-user`, payload);
  }

  /**
   * Sanction / PEP screening — SRS §M6. Runs at submission and again at each checker stage.
   * The portal consumes the outcome only; investigation happens in NCC's screening solution.
   */
  screen(payload: unknown): Observable<any> {
    return this.http.post<any>(`${this.meta}/../user/ekyc-management/screening/check`, payload);
  }

  /**
   * Risk grading — SRS §M7. Returns the numeric score, band, per-parameter breakdown and the
   * model version, all of which are shown to the Maker and both checkers.
   */
  grade(payload: unknown): Observable<any> {
    return this.http.post<any>(`${this.meta}/../user/ekyc-management/risk-grading/evaluate`, payload);
  }

  /** The active grading parameters with their selectable scores, ordered by position. */
  gradingModel(): Observable<any> {
    return this.http.get<any>(`${this.meta}/../user/ekyc-management/risk-grading/model`);
  }

  /** Upload a supporting document. Mirrors the retail journey's file-server endpoint. */
  uploadFile(payload: FormData): Observable<any> {
    return this.http.post<any>(`${this.meta}/../file-server/public/common/file/upload`, payload);
  }
}
